import { useCallback } from "react";

/**
 * Persists a refresh table's rows-per-page selection per user in localStorage so the
 * preference survives page reloads and future sessions (Redux state resets on reload).
 * Keyed by the user's agentId and by tableKey, so each table keeps its own preference
 * rather than every table on the site sharing one. Returns the persisted page size (or
 * null when absent or invalid) and a setter that writes the current selection.
 *
 * tableKey values in use: "team", "offices". The "team" key predates this parameter, so
 * it must stay spelled exactly that way to keep already-stored preferences readable.
 */
const storageKey = (userId: number, tableKey: string) => `${tableKey}TablePageSize:v1:${userId}`;

export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export const DEFAULT_PAGE_SIZE = 10;

export const usePageSizePrefs = (userId: number, tableKey: string) => {
	const readPageSizePref = useCallback((): number | null => {
		try {
			const raw = localStorage.getItem(storageKey(userId, tableKey));
			if (!raw) {
				return null;
			}
			const parsed = JSON.parse(raw);
			// Ignore sizes outside the offered options so a stale value can't strand the
			// user on a page size the dropdown can no longer display.
			if (!PAGE_SIZE_OPTIONS.includes(parsed)) {
				return null;
			}
			return parsed;
		} catch(e) {
			return null;
		}
	}, [userId, tableKey]);

	const writePageSizePref = useCallback((pageSize: number) => {
		try {
			localStorage.setItem(storageKey(userId, tableKey), JSON.stringify(pageSize));
		} catch(e) {
			// localStorage unavailable (private mode / quota) — preference simply won't persist.
		}
	}, [userId, tableKey]);

	return { readPageSizePref, writePageSizePref };
};
